if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.applyStoredQuantityRule();

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        const error = typeof data.description === 'string' ? data.description : data.message;

        if (data.status) {
          this.applyQuantityRuleFromError(error, formData);
          this.setErrorMsgState(error);
        }

        if (!response.ok) throw new Error(response.status);

        const shouldRedirectToCart = !theme.customer?.isB2B && theme.settings.afterAtc === 'page';

        if (shouldRedirectToCart) {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
          return;
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCart();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }

    getQtyInput() {
      return this.form ? this.form.querySelector('.qty-input__input[name="quantity"]') : null;
    }

    parseQuantityRuleFromError(errorText) {
      if (!errorText || typeof errorText !== 'string') return null;

      const text = errorText.toLowerCase();
      const looksLikeQtyRuleError = [
        'increment',
        'increments',
        'multiple',
        'multiples',
        'múltipl',
        'cantidad',
        'quantity',
        'mín',
        'minimum',
        'máx',
        'maximum'
      ].some((token) => text.includes(token));

      if (!looksLikeQtyRuleError) return null;

      const getNum = (regex) => {
        const match = errorText.match(regex);
        return match ? Number(match[1]) : null;
      };

      const min = getNum(/(?:mín(?:imo)?|minimum|min)[^\d]*(\d+)/i);
      const max = getNum(/(?:máx(?:imo)?|maximum|max)[^\d]*(\d+)/i);
      let increment = getNum(/(?:increment(?:o)?s?|incre(?:ment|mento))[^\d]*(\d+)/i);

      if (!increment) {
        increment = getNum(/(?:m[uú]ltipl(?:o|os|e|es)(?:\s+de)?|multiple(?:s)?(?:\s+of)?)[^\d]*(\d+)/i);
      }

      const fallbackNumbers = (errorText.match(/\d+/g) || []).map((n) => Number(n));
      if (!increment && fallbackNumbers.length === 1 && looksLikeQtyRuleError) {
        increment = fallbackNumbers[0];
      }

      if (!min && !max && !increment) return null;

      return {
        min: min || null,
        max: max || null,
        increment: increment || null
      };
    }

    applyQuantityRuleToInput(rule) {
      const qtyInput = this.getQtyInput();
      if (!qtyInput || !rule) return;

      const min = Number(rule.min) || 1;
      const step = Number(rule.increment) || 1;
      const max = rule.max == null || rule.max === '' ? null : Number(rule.max);
      const normalizedMin = step > 1 && min < step ? step : min;

      qtyInput.min = String(normalizedMin);
      qtyInput.step = String(step);
      qtyInput.dataset.qtyMin = String(normalizedMin);
      qtyInput.dataset.qtyStep = String(step);

      if (max != null && !Number.isNaN(max)) {
        qtyInput.max = String(max);
        qtyInput.dataset.qtyMax = String(max);
      } else {
        qtyInput.removeAttribute('max');
        delete qtyInput.dataset.qtyMax;
      }

      const quantityInputEl = qtyInput.closest('quantity-input');
      if (quantityInputEl && typeof quantityInputEl.normalizeInputValue === 'function') {
        quantityInputEl.normalizeInputValue('up');
        quantityInputEl.currentQty = qtyInput.value;
      } else {
        let value = Number(qtyInput.value);
        if (Number.isNaN(value) || value < normalizedMin) value = normalizedMin;
        qtyInput.value = String(value);
      }
    }

    applyQuantityRuleFromError(errorText, formData) {
      const rule = this.parseQuantityRuleFromError(errorText);
      if (!rule) return;

      if ((!rule.min || Number(rule.min) <= 1) && Number(rule.increment) > 1) {
        rule.min = Number(rule.increment);
      }

      if (!rule.increment) rule.increment = 1;
      if (!rule.min) rule.min = 1;

      this.applyQuantityRuleToInput(rule);

      const variantId = formData && formData.get('id');
      if (!variantId) return;

      try {
        sessionStorage.setItem(`b2b-qty-rule:${variantId}`, JSON.stringify(rule));
      } catch (e) {
        // no-op
      }
    }

    applyStoredQuantityRule() {
      const qtyInput = this.getQtyInput();
      const variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      if (!qtyInput || !variantInput || !variantInput.value) return;

      try {
        const raw = sessionStorage.getItem(`b2b-qty-rule:${variantInput.value}`);
        if (!raw) return;
        const rule = JSON.parse(raw);
        this.applyQuantityRuleToInput(rule);
      } catch (e) {
        // no-op
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
